/**
 * The Geospatial value types and their one reader. `GeoValue` is the inner
 * tagged union of a `complex_property` row's `{ tag: 'Geospatial' }` value;
 * `partsOf` turns any of its six variants into vertex lists. Pure, with no MUI
 * and no React, so the `./map` entry can use it. `geoShape.ts` builds the
 * writers and the menus on it and re-exports these for the main entry.
 */

/** One vertex in WGS84 degrees; `elev` metres, absent when the value has no altitude. */
export interface GeoVertex {
  lat: number;
  lon: number;
  elev?: number | null;
}

/** `GeoPosition` as the generated bindings shape it (altitude is an Option). */
export interface GeoPosition {
  longitude: number;
  latitude: number;
  altitude?: number | null;
}

type Ring = { positions: GeoPosition[] };

/** The inner Geospatial value, `{ tag, value }`, as stored and as the reducer takes it. */
export type GeoValue =
  | { tag: 'Point'; value: { coordinates: GeoPosition } }
  | { tag: 'MultiPoint'; value: { coordinates: GeoPosition[] } }
  | { tag: 'LineString'; value: { coordinates: GeoPosition[] } }
  | { tag: 'MultiLineString'; value: { lineStrings: Ring[] } }
  | { tag: 'Polygon'; value: { rings: Ring[] } }
  | { tag: 'MultiPolygon'; value: { polygons: { rings: Ring[] }[] } };

const vertex = (p: GeoPosition): GeoVertex => ({
  lat: p.latitude,
  lon: p.longitude,
  elev: p.altitude ?? null,
});

/** Whether a position list closes on itself: at least 4 positions, the last
 *  equal to the first in latitude and longitude (altitude is not compared). */
export function isClosedPath(vs: readonly { lat: number; lon: number }[]): boolean {
  const first = vs[0];
  const last = vs[vs.length - 1];
  return vs.length >= 4 && first.lat === last.lat && first.lon === last.lon;
}

/** Every part of any variant as a vertex list: a Point is one list of one, a
 *  MultiPoint one list per point, a Polygon one list per ring, a MultiPolygon
 *  every ring of every polygon. What both renderers draw. Unknown: []. */
export function partsOf(value: GeoValue | null | undefined): GeoVertex[][] {
  switch (value?.tag) {
    case 'Point':
      return [[vertex(value.value.coordinates)]];
    case 'MultiPoint':
      return value.value.coordinates.map((p) => [vertex(p)]);
    case 'LineString':
      return [value.value.coordinates.map(vertex)];
    case 'MultiLineString':
      return value.value.lineStrings.map((l) => l.positions.map(vertex));
    case 'Polygon':
      return value.value.rings.map((r) => r.positions.map(vertex));
    case 'MultiPolygon':
      return value.value.polygons.flatMap((p) => p.rings.map((r) => r.positions.map(vertex)));
    default:
      return [];
  }
}
